// IMPORTANT: @highcharts/react is pinned to an exact version in package.json
// because this file patches Highcharts internals (Series.prototype.destroy, Chart 'load'
// event). Minor version bumps could rename or remove these APIs. Upgrade manually and
// verify the patches still work against the new version's source.
import Highcharts from 'highcharts';

/**
 * Guard against "Cannot read properties of undefined (reading 'chart')" crash.
 *
 * Root cause: @highcharts/react v5's Chart component calls chart.update(config, true, true)
 * on every re-render where the computed chart config changes. The third argument (oneToOne)
 * tells Highcharts to match series 1:1 — destroying excess old series and creating new ones.
 * When Apollo polling delivers new data that changes the number of visible hub series (hubs
 * going online/offline, or telemetry arriving for a different set of hubs), the update cycle
 * iterates the old series array and calls series.destroy() on removed entries. If a series
 * was already partially detached (its .chart reference set to undefined by a prior destroy
 * in the same update cycle), the second destroy() throws.
 *
 * Fix: Wrap Series.prototype.destroy so it no-ops when .chart is already undefined.
 * This is safe because destroy() without a chart reference has nothing to clean up.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- intentionally retained: patching Highcharts internal prototype
const origSeriesDestroy = (Highcharts as any).Series.prototype.destroy;
// oxlint-disable-next-line typescript/no-explicit-any -- intentionally retained: Highcharts internal prototype patch
(Highcharts as any).Series.prototype.destroy = function (...args: any[]) {
  if (!this.chart) {
    return;
  }
  return origSeriesDestroy.apply(this, args);
};

/**
 * Guard Point.prototype.destroy against the same race condition.
 *
 * When switching time periods (1D → 1W → 1M), chart.update() calls series.setData()
 * which destroys old points. If the series was already detached mid-update cycle
 * (series count changed), point.destroy() accesses point.series.chart which is
 * undefined. This causes "Cannot read properties of undefined (reading 'chart')"
 * at Point.js:358.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- intentionally retained: patching Highcharts internal prototype
const origPointDestroy = (Highcharts as any).Point.prototype.destroy;
// oxlint-disable-next-line typescript/no-explicit-any -- intentionally retained: Highcharts internal prototype patch
(Highcharts as any).Point.prototype.destroy = function (...args: any[]) {
  if (!this.series || !this.series.chart) {
    return;
  }
  return origPointDestroy.apply(this, args);
};

/**
 * Auto-destroy zombie Highcharts chart instances when their container is removed from the DOM.
 *
 * Problem: @highcharts/react v5's Chart component has NO useEffect cleanup — it never calls
 * chart.destroy() on unmount. When the dashboard component unmounts (navigating to Settings,
 * Storage Periods, etc.), React removes the container <div> from the DOM, but the chart
 * instance survives in memory. Apollo polling continues to trigger cache broadcasts →
 * React re-renders → chart.update() fires on the orphaned chart → Highcharts renders into
 * a detached DOM tree, burning CPU on invisible SVG layout calculations.
 *
 * Measured impact: 12 zombie charts generating 434,580 DOM mutations/min on detached nodes,
 * causing 10.8 long tasks/second on the Storage Periods page (which has no charts).
 *
 * Fix: Use a MutationObserver on <body> to detect when Highcharts container elements
 * (divs with a child .highcharts-container) are removed from the DOM. When detected,
 * find the chart instance and call chart.destroy() to clean up timers, event listeners,
 * and internal state.
 *
 * Highcharts stores chart instances in an internal registry keyed by the container element.
 * We use chart.renderTo to match removed containers to their chart instances.
 */
if (typeof document !== 'undefined') {
  // Track all chart instances created by @highcharts/react
  // oxlint-disable-next-line typescript/no-explicit-any -- intentionally retained: Highcharts internal API
  const chartInstances = new Set<any>();

  Highcharts.addEvent(Highcharts.Chart, 'load', function () {
    chartInstances.add(this);
  });

  Highcharts.addEvent(Highcharts.Chart, 'destroy', function () {
    chartInstances.delete(this);
  });

  // Observe DOM removals — when a chart's container is removed, destroy the chart
  const zombieObserver = new MutationObserver((mutations) => {
    // Fast path: skip all processing when no chart instances are tracked.
    // This eliminates overhead on pages without charts (Settings, Tickets, etc.)
    // where the observer still fires on AG Grid virtual scroll and form mutations.
    if (chartInstances.size === 0) return;

    for (const mutation of mutations) {
      if (mutation.type !== 'childList' || mutation.removedNodes.length === 0) continue;

      for (const node of mutation.removedNodes) {
        if (!(node instanceof HTMLElement)) continue;

        // Instead of querySelectorAll (which some TS lib versions mark as deprecated
        // on Element), check directly against tracked chart instances — more efficient
        // since we already maintain the Set and avoid a DOM query on a detached tree.
        // Safe: ES2015 spec guarantees that deleting a Set entry during for...of
        // iteration will not cause it to be visited again. The 'destroy' event
        // listener calls chartInstances.delete(this), which is a supported
        // mid-iteration mutation per ECMA-262 §24.2.5.4.
        for (const chart of chartInstances) {
          if (!chart.renderTo) continue;
          // node.contains covers all depths (including direct parent), but the
          // parentElement check makes the "React removed the wrapper div" case
          // visually explicit for future readers.
          if (node.contains(chart.renderTo) || node === chart.renderTo.parentElement) {
            try {
              chart.destroy();
            } catch {
              // Chart may already be partially destroyed — safe to ignore
            }
          }
        }
      }
    }
  });

  zombieObserver.observe(document.body, { childList: true, subtree: true });
  // PERF NOTE: body + subtree fires on all DOM mutations. The chartInstances.size
  // guard above (added after profiling) ensures zero-cost on chart-free pages.
  // On chart pages, the callback processes ~1-5 mutations per navigation event
  // (React's batch DOM removal) — negligible compared to the 434k mutations/min
  // the zombie charts themselves would cause without this cleanup.
}

/**
 * Configure Highcharts to use the given IANA timezone for datetime axis rendering.
 * Highcharts v12+ uses the native `time.timezone` option (IANA string) instead of
 * the removed `time.getTimezoneOffset` callback.
 *
 * Call this once per chart component before building the options object.
 */
export const setHighchartsTimezone = (timezone: string): void => {
  Highcharts.setOptions({
    time: {
      timezone,
    },
  });
};

/**
 * Mobile touch-scroll fix for Highcharts v13.
 *
 * Root cause: Highcharts' Pointer.touch() calls e.preventDefault() on touchstart
 * and touchmove events, which blocks page scrolling. However, we still want the
 * tooltip to follow the finger (Highcharts' internal tracking logic).
 *
 * Fix: Monkey-patch Event.prototype.preventDefault so that when called on touch
 * events whose target is inside a .highcharts-container, it becomes a no-op.
 * This allows Highcharts' touch handlers to run normally (tooltip follows finger)
 * but removes the scroll-blocking side effect.
 *
 * Trade-off: Pinch-to-zoom on charts is disabled on mobile (the browser handles
 * pinch as page zoom instead). This is acceptable since our charts don't use
 * zooming.
 */

/**
 * Touch device detection — defense-in-depth with three independent checks:
 *
 * 1. `'ontouchstart' in window` — Touch Events API is available in this browser.
 * 2. `navigator.maxTouchPoints > 0` — hardware reports touch capability (covers
 *    PointerEvents-only browsers where 'ontouchstart' may be absent).
 * 3. `window.matchMedia('(hover: none)').matches` — the PRIMARY input device has
 *    no hover capability. This is the crucial discriminator that excludes desktop
 *    touchscreen laptops (Surface Pro, etc.) where the primary input is a
 *    mouse/trackpad. On those devices, maxTouchPoints > 0 is true but (hover: none)
 *    is false — without this check, the patch would incorrectly activate.
 *
 * All three together ensure the patch only runs on phones/tablets where touch is
 * the primary interaction and scrolling over charts is the expected gesture.
 */
const isTouchDevice =
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0) &&
  window.matchMedia('(hover: none)').matches;

if (isTouchDevice && typeof document !== 'undefined') {
  // oxlint-disable-next-line typescript/unbound-method -- intentional: capturing prototype method for monkey-patch, called with correct this via .call()
  const origPreventDefault = Event.prototype.preventDefault;

  Event.prototype.preventDefault = function (this: Event): void {
    // Only intercept touch events targeting Highcharts containers
    if (
      (this.type === 'touchstart' || this.type === 'touchmove') &&
      // Touch targets on Highcharts SVG charts are always Element instances
      // (SVGElement extends Element). If a Text node is somehow the target,
      // we correctly fall through to the original preventDefault — Highcharts'
      // Pointer.touch() only operates on container Elements, not raw text nodes.
      this.target instanceof Element &&
      this.target.closest('.highcharts-container')
    ) {
      // No-op: let the browser handle the gesture (scroll) while Highcharts
      // still processes the event for tooltip/crosshair tracking
      return;
    }
    origPreventDefault.call(this);
  };
}

/**
 * Dismiss ghost tooltips on page scroll (outside: true workaround).
 *
 * When tooltip.outside=true, Highcharts appends a <div class="highcharts-tooltip-container">
 * to document.body. On touch devices, scrolling the page doesn't trigger Highcharts'
 * pointerleave, so the tooltip stays visible after the chart scrolls away.
 * This is a known Highcharts limitation (issues #11635, #20969) with no native fix
 * as of v13.0.0 (July 2026).
 *
 * Note: @highcharts/react v5 (declarative JSX API) does NOT populate the global
 * Highcharts.charts array, so we cannot use chart.pointer.reset(). Instead we
 * manipulate the tooltip container DOM directly.
 *
 * Fix: After 100px of scroll, move all tooltip containers off-screen via CSS
 * transform. This visually hides them without affecting Highcharts' internal
 * visibility/display state. When the user next interacts with a chart, Highcharts
 * repositions the container with inline styles, overriding our transform.
 */
if (typeof window !== 'undefined') {
  let lastScrollY = window.scrollY;

  window.addEventListener(
    'scroll',
    () => {
      const delta = Math.abs(window.scrollY - lastScrollY);
      if (delta < 100) return;

      lastScrollY = window.scrollY;

      const containers = document.querySelectorAll('.highcharts-tooltip-container');
      for (const el of containers) {
        // Move off-screen. Highcharts overwrites `left`/`top` on next tooltip show,
        // which naturally brings it back into view. This avoids desynchronizing
        // Highcharts' internal isHidden/visibility state.
        (el as HTMLElement).style.left = '-9999px';
        (el as HTMLElement).style.top = '-9999px';
      }
    },
    { passive: true }
  );

  /**
   * Clean up orphaned tooltip containers when charts are destroyed.
   * See issue #18490 — tooltip.outside containers aren't always removed on destroy.
   */
  Highcharts.addEvent(Highcharts.Chart, 'destroy', function () {
    // oxlint-disable-next-line typescript/no-explicit-any -- Highcharts internal: tooltip.container is not in public typings
    const tooltipContainer = (this as any).tooltip?.container;
    if (tooltipContainer && tooltipContainer.parentNode) {
      tooltipContainer.parentNode.removeChild(tooltipContainer);
    }
  });
}
